import React, { useState } from 'react';
import { Send, CheckCircle, User, Building, Globe, Target, DollarSign, Calendar, MessageSquare, Phone, Mail } from 'lucide-react';
import { analytics } from '../utils/analytics';
import { sendFormSubmission, fallbackToMailto } from '../utils/emailService';

export const GetStarted: React.FC = () => {
  const [formData, setFormData] = useState({
    // Contact Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Business Information
    companyName: '',
    industry: '',
    companySize: '',
    currentWebsite: '',
    
    // Project Information
    projectType: '',
    projectGoals: [],
    timeline: '',
    budget: '',
    
    // Website Information
    hasExistingWebsite: '',
    currentWebsiteUrl: '',
    websiteLikes: '',
    websiteDislikes: '',
    competitorWebsites: '',
    
    // Features & Functionality
    requiredFeatures: [],
    contentManagement: '',
    ecommerce: '',
    integrations: '',
    
    // Design Preferences
    designStyle: '',
    colorPreferences: '',
    brandGuidelines: '',
    
    // Additional Information
    additionalRequirements: '',
    questionsForUs: '',
    howDidYouHear: '',
    preferredContactMethod: '',
    bestTimeToCall: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      if (checkbox.checked) {
        setFormData(prev => ({
          ...prev,
          [name]: [...(prev[name as keyof typeof prev] as string[]), value]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: (prev[name as keyof typeof prev] as string[]).filter(item => item !== value)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Track get started form submission
      analytics.trackGetStarted();

      // Try to send via email service first
      const result = await sendFormSubmission({
        formType: 'get-started',
        formData
      });

      if (result.success) {
        setIsSubmitted(true);
        setSubmitMessage(result.message);
        
        // Reset form after 10 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            firstName: '', lastName: '', email: '', phone: '', companyName: '', industry: '', companySize: '', currentWebsite: '',
            projectType: '', projectGoals: [], timeline: '', budget: '', hasExistingWebsite: '', currentWebsiteUrl: '',
            websiteLikes: '', websiteDislikes: '', competitorWebsites: '', requiredFeatures: [], contentManagement: '',
            ecommerce: '', integrations: '', designStyle: '', colorPreferences: '', brandGuidelines: '',
            additionalRequirements: '', questionsForUs: '', howDidYouHear: '', preferredContactMethod: '', bestTimeToCall: ''
          });
        }, 10000);
      } else {
        // If email service fails, fall back to mailto
        setSubmitMessage('Redirecting to your email client...');
        setTimeout(() => {
          fallbackToMailto('get-started', formData);
          setIsSubmitted(true);
          setSubmitMessage('Your email client should now be open with your project information. Please send the email to complete your inquiry.');
        }, 1000);
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      // Fall back to mailto on any error
      setSubmitMessage('Redirecting to your email client...');
      setTimeout(() => {
        fallbackToMailto('get-started', formData);
        setIsSubmitted(true);
        setSubmitMessage('Your email client should now be open with your project information. Please send the email to complete your inquiry.');
      }, 1000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectGoalOptions = [
    'Increase online sales',
    'Generate more leads',
    'Improve brand awareness',
    'Better user experience',
    'Mobile optimization',
    'Search engine optimization',
    'Modernize existing website',
    'Launch new business online',
    'Improve conversion rates',
    'Integrate with business systems'
  ];

  const requiredFeatureOptions = [
    'Contact forms',
    'Online booking/scheduling',
    'E-commerce/online store',
    'Blog/news section',
    'Photo galleries',
    'Video integration',
    'Social media integration',
    'Customer portal/login',
    'Search functionality',
    'Multi-language support',
    'Live chat',
    'Newsletter signup',
    'Testimonials/reviews',
    'Event calendar',
    'Document downloads'
  ];

  return (
    <div className="w-full overflow-hidden">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Target className="h-16 w-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Get Started
            </h1>
            <p className="text-xl sm:text-2xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Tell us about your project and goals. We'll use this information to prepare for our initial consultation and provide you with the best possible solution.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {isSubmitted ? (
            <div className="card p-12 text-center">
              <CheckCircle className="h-20 w-20 text-accent-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-xl text-gray-600 mb-6">
                {submitMessage}
              </p>
              <p className="text-sm text-gray-500">
                If you have any urgent questions, feel free to call us at (586) 248-4115.
              </p>
            </div>
          ) : (
            <div className="card p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Project Information Form
                </h2>
                <p className="text-gray-600">
                  Please provide your contact information (required) and any additional details that will help us understand your needs.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact Information */}
                <div className="border-b border-gray-200 pb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <User className="h-6 w-6 text-primary-600 mr-3" />
                    Contact Information
                    <span className="text-red-500 ml-2">*</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div className="border-b border-gray-200 pb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Building className="h-6 w-6 text-primary-600 mr-3" />
                    Business Information
                    <span className="text-gray-500 text-sm ml-2 font-normal">(Optional)</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      >
                        <option value="">Select your industry</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Technology">Technology</option>
                        <option value="Retail/E-commerce">Retail/E-commerce</option>
                        <option value="Professional Services">Professional Services</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Education">Education</option>
                        <option value="Non-profit">Non-profit</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Food & Beverage">Food & Beverage</option>
                        <option value="Automotive">Automotive</option>
                        <option value="Financial Services">Financial Services</option>
                        <option value="Construction">Construction</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size
                      </label>
                      <select
                        id="companySize"
                        name="companySize"
                        value={formData.companySize}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      >
                        <option value="">Select company size</option>
                        <option value="Solo/Freelancer">Solo/Freelancer</option>
                        <option value="2-10 employees">2-10 employees</option>
                        <option value="11-50 employees">11-50 employees</option>
                        <option value="51-200 employees">51-200 employees</option>
                        <option value="200+ employees">200+ employees</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="currentWebsite" className="block text-sm font-medium text-gray-700 mb-2">
                        Current Website URL
                      </label>
                      <input
                        type="url"
                        id="currentWebsite"
                        name="currentWebsite"
                        value={formData.currentWebsite}
                        onChange={handleChange}
                        placeholder="https://yourwebsite.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>

                {/* Project Information */}
                <div className="border-b border-gray-200 pb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Target className="h-6 w-6 text-primary-600 mr-3" />
                    Project Information
                    <span className="text-gray-500 text-sm ml-2 font-normal">(Optional)</span>
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                        Project Type
                      </label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      >
                        <option value="">Select project type</option>
                        <option value="New website design and development">New website design and development</option>
                        <option value="Website redesign">Website redesign</option>
                        <option value="E-commerce store">E-commerce store</option>
                        <option value="Website maintenance and updates">Website maintenance and updates</option>
                        <option value="SEO and digital marketing">SEO and digital marketing</option>
                        <option value="Mobile app development">Mobile app development</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Project Goals (select all that apply)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {projectGoalOptions.map((goal) => (
                          <label key={goal} className="flex items-center">
                            <input
                              type="checkbox"
                              name="projectGoals"
                              value={goal}
                              checked={formData.projectGoals.includes(goal)}
                              onChange={handleChange}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              disabled={isSubmitting}
                            />
                            <span className="ml-2 text-sm text-gray-700">{goal}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                          Desired Timeline
                        </label>
                        <select
                          id="timeline"
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                          disabled={isSubmitting}
                        >
                          <option value="">Select timeline</option>
                          <option value="ASAP (Rush job)">ASAP (Rush job)</option>
                          <option value="Within 1 month">Within 1 month</option>
                          <option value="1-3 months">1-3 months</option>
                          <option value="3-6 months">3-6 months</option>
                          <option value="6+ months">6+ months</option>
                          <option value="Flexible">Flexible</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                          Budget Range
                        </label>
                        <select
                          id="budget"
                          name="budget"
                          value={formData.budget}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                          disabled={isSubmitting}
                        >
                          <option value="">Select budget range</option>
                          <option value="Under $2,500">Under $2,500</option>
                          <option value="$2,500 - $5,000">$2,500 - $5,000</option>
                          <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                          <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                          <option value="$25,000+">$25,000+</option>
                          <option value="Need consultation">Need consultation</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="pb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <MessageSquare className="h-6 w-6 text-primary-600 mr-3" />
                    Additional Information
                    <span className="text-gray-500 text-sm ml-2 font-normal">(Optional)</span>
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="additionalRequirements" className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Requirements or Special Considerations
                      </label>
                      <textarea
                        id="additionalRequirements"
                        name="additionalRequirements"
                        rows={4}
                        value={formData.additionalRequirements}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        placeholder="Anything else we should know about your project?"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="questionsForUs" className="block text-sm font-medium text-gray-700 mb-2">
                        Questions for Our Team
                      </label>
                      <textarea
                        id="questionsForUs"
                        name="questionsForUs"
                        rows={3}
                        value={formData.questionsForUs}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        placeholder="What questions do you have for us?"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="howDidYouHear" className="block text-sm font-medium text-gray-700 mb-2">
                        How did you hear about us?
                      </label>
                      <select
                        id="howDidYouHear"
                        name="howDidYouHear"
                        value={formData.howDidYouHear}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        disabled={isSubmitting}
                      >
                        <option value="">Select option</option>
                        <option value="Google search">Google search</option>
                        <option value="Social media">Social media</option>
                        <option value="Referral from friend/colleague">Referral from friend/colleague</option>
                        <option value="Previous client">Previous client</option>
                        <option value="Online advertisement">Online advertisement</option>
                        <option value="Industry publication">Industry publication</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {submitMessage && !isSubmitted && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-blue-700">{submitMessage}</p>
                  </div>
                )}

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`btn-primary text-lg px-6 py-4 w-full sm:w-auto ${
                      isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2 inline-block"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Project Information
                        <Send className="ml-2 h-5 w-5 inline-block" />
                      </>
                    )}
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    We'll review your information and contact you within 24 hours to schedule your free consultation.
                  </p>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Happens Next?
            </h2>
            <p className="text-xl text-gray-600">
              Here's what you can expect after submitting your project information.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">1. We Review</h3>
              <p className="text-gray-600">
                Our sales team carefully reviews your project information and prepares for your consultation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="h-8 w-8 text-accent-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">2. We Contact You</h3>
              <p className="text-gray-600">
                We'll reach out within 24 hours to schedule your free consultation at a convenient time.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">3. We Plan Together</h3>
              <p className="text-gray-600">
                During our consultation, we'll discuss your goals and create a custom solution for your project.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Have Questions? Contact Us Directly
          </h3>
          <p className="text-gray-600 mb-6">
            If you prefer to speak with us directly or have urgent questions, don't hesitate to reach out.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:5862484115" 
              onClick={() => analytics.trackPhoneCall()}
              className="btn-primary px-6 py-3"
            >
              <Phone className="h-5 w-5 mr-2" />
              Call (586) 248-4115
            </a>
            <a 
              href="mailto:sales@outstandingnet.com" 
              onClick={() => analytics.trackEmailClick()}
              className="btn-outline px-6 py-3"
            >
              <Mail className="h-5 w-5 mr-2" />
              Email Sales Team
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};